<template>
  <nav
    class="fixed left-0 top-0 z-[1100] flex h-screen w-[12.625rem] flex-col"
    @focusin="setSidebarExpanded(true)"
    @focusout="handleSidebarFocusOut"
  >
    <div class="flex-1 pl-0 pr-0 py-0">
      <div
        class="panel-neo panel-neo--glassier flex h-full flex-col rounded-none py-5 overflow-hidden transition-[width,padding] duration-120 ease-out will-change-[width]"
        :class="[isSidebarExpanded ? 'w-full px-0' : 'w-[70px] px-0']"
        @mouseenter="handleSidebarMouseEnter"
        @mouseleave="handleSidebarMouseLeave"
      >
        <div class="mb-9">
          <router-link to="/dashboard" class="relative block group">
            <span
              class="absolute inset-0 rounded-lg transition-opacity duration-120 opacity-0 group-hover:opacity-100 group-hover:bg-slate-800/60"
            ></span>
            <span
              class="relative z-[1] grid grid-cols-[70px_minmax(0,1fr)] items-center py-2 text-sm font-light text-slate-300 transition-colors duration-200 rounded-lg overflow-hidden group-hover:text-white"
            >
              <img
                src="/svg-cons/testdrive-logo-no-badge.png"
                alt="TestDrive logo"
                class="h-[34px] w-[34px] shrink-0 object-contain justify-self-center"
              />
              <span
                class="whitespace-nowrap shrink-0 overflow-hidden transition-opacity duration-100 ease-out pr-3"
                :class="isSidebarExpanded ? 'w-auto opacity-100' : 'w-0 opacity-0'"
              >
                Test<span class="italic">Drive</span>
              </span>
            </span>
          </router-link>
        </div>

        <nav class="space-y-[9px] relative" ref="navRoot">
          <span
            ref="indicator"
            class="hidden pointer-events-none absolute left-2 w-[calc(100%-1rem)] h-8 rounded-lg transition-all duration-300"
            style="opacity: 0"
          ></span>
          <router-link
            v-for="item in navigation"
            :key="item.name"
            :to="'/' + item.key"
            class="relative block group"
          >
            <span
              class="absolute inset-0 rounded-lg transition-opacity duration-120 opacity-0 group-hover:opacity-100 group-hover:bg-slate-800/60"
            ></span>
            <span
              :class="[
                'relative z-[1] grid grid-cols-[70px_minmax(0,1fr)] items-center py-2 text-sm font-light transition-colors duration-200 rounded-lg overflow-hidden',
                activeKey === item.key ? 'text-white' : 'text-slate-300 group-hover:text-white',
              ]"
            >
              <img
                :src="item.iconSrc"
                :alt="item.name + ' icon'"
                class="shrink-0"
                :class="[
                  'justify-self-center',
                  item.key === 'off-road'
                    ? 'w-10 h-10'
                    : item.key === 'dashboard'
                      ? 'w-[34px] h-[34px]'
                      : 'w-[30px] h-[30px]',
                  activeKey === item.key ? 'sidebar-icon-active' : '',
                ]"
              />
              <span
                class="whitespace-nowrap shrink-0 overflow-hidden transition-opacity duration-100 ease-out pr-3"
                :class="isSidebarExpanded ? 'w-auto opacity-100' : 'w-0 opacity-0'"
              >
                {{ item.name }}
              </span>
            </span>
          </router-link>

          <!-- Admin panels at bottom -->
          <div class="mt-6 space-y-2">
            <!-- Admin Tools (Equipment Catalog, Drafts, Products, Warehouse) — admin only -->
            <AdminToolsSlideout
              v-if="showAdminTools"
              title="Admin Tools"
              icon="/svg-icons/admin-tools.svg"
              :is-expanded="isSidebarExpanded"
              :links="adminToolsLinks"
              @toggle="emit('adminToolsToggle', $event)"
            />
            <!-- Admin (Users, Roles, Security) — admin + read-only support roles -->
            <AdminSlideout
              v-if="showAdminSection"
              :is-expanded="isSidebarExpanded"
              icon-src="/svg-icons/key2.svg"
              @admin-slideout-toggle="emit('adminSlideoutToggle', $event)"
            />
          </div>
        </nav>
        <!-- User Profile -->
        <div class="mt-auto pt-4">
          <div class="relative w-full" ref="profileMenuContainer">
            <div
              class="w-full max-w-full bg-slate-800/30 rounded-lg p-3 border border-slate-700/50 box-border"
              :class="[
                currentUser.email && isSidebarExpanded ? 'cursor-pointer hover:bg-slate-800/45 transition-colors' : '',
                isSidebarExpanded ? 'p-1' : 'p-0.5',
              ]"
              @click="toggleProfileMenu"
            >
              <div class="flex items-center" :class="isSidebarExpanded ? 'space-x-3 mb-3' : 'justify-center mb-0'">
              <div
                class="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center ring-2 ring-slate-600/20"
              >
                <span class="text-xs font-bold text-white">{{ currentUser.initials }}</span>
              </div>
              <div v-if="isSidebarExpanded" class="flex-1 min-w-0 flex flex-col justify-center">
                <div class="flex items-center gap-2">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-600/20 border border-primary-500/30 text-[10px] text-primary-300 uppercase tracking-wide font-medium"
                  >
                    {{ currentUser.role }}
                  </span>
                </div>
                <p class="text-xs text-slate-400 truncate mt-[3px]">{{ currentUser.email }}</p>
              </div>
            </div>
              <div v-if="isSidebarExpanded" class="mt-2 space-y-1.5">
                <router-link
                  v-if="!currentUser.email"
                  to="/signin"
                  class="w-full text-center py-2 px-3 text-xs text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-md transition-colors inline-flex items-center justify-center gap-2"
                >
                  <img src="/images/sign-in-white.svg" class="w-4 h-4" alt="" />
                  Sign In
                </router-link>
              </div>
            </div>

            <div
              v-if="isSidebarExpanded && showProfileMenu && currentUser.email"
              class="absolute bottom-full left-0 right-0 mb-2 z-[13000]"
            >
              <div
                class="w-full rounded-lg border border-slate-700/70 bg-slate-900/95 p-3 shadow-2xl"
                @click.stop
              >
                <div class="grid grid-cols-2 gap-2">
                  <button
                    @click="handleOpenAlertPreferences"
                    class="flex-1 py-2 px-3 text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-md transition-colors inline-flex items-center justify-center"
                    title="Alert Settings"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Alert settings">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </button>
                  <button
                    @click="handleSignOut"
                    class="flex-1 py-2 px-3 text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded-md transition-colors inline-flex items-center justify-center"
                    title="Sign Out"
                  >
                    <img src="/images/sign-out-white.svg" class="w-4 h-4" alt="" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, watch, ref, nextTick, computed } from 'vue'
import { useRoute } from 'vue-router'
import AdminSlideout from './AdminSlideout.vue'
import AdminToolsSlideout from './AdminToolsSlideout.vue'
import { useAuth0Store } from '@/stores/auth0'

type NavItem = { key: string; name: string; icon?: string; iconSrc?: string }
type CurrentUser = { initials: string; name: string; role: string; email?: string }

const props = defineProps<{
  navigation: NavItem[]
  activeTab: string
  currentUser: CurrentUser
}>()

const emit = defineEmits<{
  (e: 'setActiveTab', key: string): void
  (e: 'openModal', type: 'equipment' | 'booking' | 'user'): void
  (e: 'signOut'): void
  (e: 'openAlertPreferences'): void
  (e: 'adminSlideoutToggle', open: boolean): void
  (e: 'adminToolsToggle', open: boolean): void
}>()

const auth0Store = useAuth0Store()

/**
 * Admin Tools slideout (Equipment Catalog, Drafts, Product Catalog, Warehouse):
 * Visible only to users with admin role — these are mutation-heavy management tools.
 */
const showAdminTools = computed(() => auth0Store.isAdmin)

/**
 * Admin slideout (Users, Roles, Security):
 * Visible to admins AND read-only support roles that have view_users permission.
 * Support roles get read-only access to these pages for monitoring/oversight.
 */
const showAdminSection = computed(() => {
  return auth0Store.isAdmin || auth0Store.hasPermission('view_users')
})

const indicator = ref<HTMLElement | null>(null)
const navRoot = ref<HTMLElement | null>(null)
const profileMenuContainer = ref<HTMLElement | null>(null)
const showProfileMenu = ref(false)
const isSidebarExpanded = ref(false)
const expandTimer = ref<number | null>(null)
const collapseTimer = ref<number | null>(null)
const adminToolsLinks = [
  {
    to: '/admin-equipment',
    title: 'Equipment Catalog',
    description: 'Edit equipment types and publish new ones',
    iconSrc: '/images/sidebar-equipment-white.svg',
  },
  {
    to: '/admin-equipment-drafts',
    title: 'Drafts',
    description: 'Resume and promote saved equipment drafts',
    iconSrc: '/images/sidebar-draft-white.svg',
  },
  {
    to: '/admin-products',
    title: 'Product Catalog',
    description: 'Manage product tags and metadata',
    iconSrc: '/images/sidebar-products-white.svg',
  },
  {
    to: '/admin-warehouse',
    title: 'Warehouse Address',
    description: 'Set the ship-from origin for labels',
    iconSrc: '/images/sidebar-security-white.svg',
  },
  {
    to: '/admin-shipping',
    title: 'Shipping Operations',
    description: 'Manage shipments, refunds, and issues',
    iconSrc: '/images/sidebar-tracking-white.svg',
  },
  {
    to: '/admin-spotter-criteria',
    title: 'Spotter Criteria',
    description: 'View Lookout/Spotter risk rules and thresholds',
    iconSrc: '/svg-icons/sidebar-lookout.svg',
  },
]
const route = useRoute()
const activeKey = computed(() => {
  const seg = (route.path.split('/')[1] || '').trim()
  return seg || 'dashboard'
})

function moveIndicator() {
  const el = indicator.value
  const root = navRoot.value
  if (!el || !root) return
  // Limit selection to direct child links of this nav only (exclude admin slideout links)
  const links = Array.from(root.querySelectorAll(':scope > a.router-link-active')) as HTMLElement[]
  const active = links[0]
  if (!active) {
    el.style.opacity = '0'
    return
  }
  const r = active.getBoundingClientRect()
  el.style.top = `${active.offsetTop + 4}px`
  el.style.height = `${r.height - 8}px`
  el.style.opacity = '1'
}

onMounted(() => {
  nextTick(moveIndicator)
  document.addEventListener('click', handleDocumentClick)
})
watch(
  () => route.fullPath,
  () => {
    setSidebarExpanded(false)
    nextTick(moveIndicator)
  },
)
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
  if (expandTimer.value !== null) {
    window.clearTimeout(expandTimer.value)
    expandTimer.value = null
  }
  if (collapseTimer.value !== null) {
    window.clearTimeout(collapseTimer.value)
    collapseTimer.value = null
  }
})

function handleDocumentClick(event: MouseEvent) {
  if (!showProfileMenu.value) return
  const container = profileMenuContainer.value
  if (!container) return
  const target = event.target as Node | null
  if (!target || container.contains(target)) return
  showProfileMenu.value = false
}

function toggleProfileMenu() {
  if (!props.currentUser.email || !isSidebarExpanded.value) return
  showProfileMenu.value = !showProfileMenu.value
}

function setSidebarExpanded(expanded: boolean) {
  isSidebarExpanded.value = expanded
  if (!expanded) {
    showProfileMenu.value = false
  }
}

function handleSidebarMouseEnter() {
  if (collapseTimer.value !== null) {
    window.clearTimeout(collapseTimer.value)
    collapseTimer.value = null
  }
  if (expandTimer.value !== null) return
  expandTimer.value = window.setTimeout(() => {
    setSidebarExpanded(true)
    expandTimer.value = null
  }, 35)
}

function handleSidebarMouseLeave() {
  if (expandTimer.value !== null) {
    window.clearTimeout(expandTimer.value)
    expandTimer.value = null
  }
  if (collapseTimer.value !== null) return
  collapseTimer.value = window.setTimeout(() => {
    setSidebarExpanded(false)
    collapseTimer.value = null
  }, 75)
}

function handleSidebarFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget as Node | null
  const currentTarget = event.currentTarget as HTMLElement | null
  if (nextTarget && currentTarget?.contains(nextTarget)) return
  setSidebarExpanded(false)
}

function handleOpenAlertPreferences() {
  showProfileMenu.value = false
  emit('openAlertPreferences')
}

function handleSignOut() {
  showProfileMenu.value = false
  emit('signOut')
}
</script>

<style scoped>
.sidebar-icon-active {
  /* Approximate icon stroke tint to RGB(255,255,0) while keeping labels unchanged. */
  filter: brightness(0) saturate(100%) invert(92%) sepia(96%) saturate(6137%) hue-rotate(2deg) brightness(106%) contrast(106%);
}
</style>
